import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useTheme } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Zap, TrendingUp, TrendingDown, Plus, RefreshCw,
  CheckCircle2, XCircle, Trash2, PenSquare, CheckSquare,
  Square, Filter, Search, ShieldCheck, AlertTriangle,
  SlidersHorizontal, Sparkles, Clock, Target, Shield, ArrowUpRight,
  ArrowDownRight, Check, X, Edit3, ChevronRight, ChevronLeft,
  ChevronsLeft, ChevronsRight, Eye, Layers, RotateCcw
} from "lucide-react";
import { toast } from "sonner";
import CloseTradeModal from "@/components/drishti/CloseTradeModal";

const asArray = (data, key) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data[key])) return data[key];
  return [];
};

// Real-time Risk-to-Reward calculator helper
const calculateRR = (entry, sl, target, zoneType) => {
  const e = parseFloat(entry);
  const s = parseFloat(sl);
  const t = parseFloat(target);
  if (isNaN(e) || isNaN(s) || isNaN(t) || e <= 0 || s <= 0 || t <= 0) {
    return { rr: "—", risk: 0, reward: 0, riskPct: "0.00", rewardPct: "0.00", isValid: true, error: "" };
  }
  
  const isDemand = (zoneType || "DEMAND") === "DEMAND";
  const risk = isDemand ? e - s : s - e;
  const reward = isDemand ? t - e : e - t;
  const riskPct = e > 0 ? (risk / e) * 100 : 0;
  const rewardPct = e > 0 ? (reward / e) * 100 : 0;
  
  if (risk <= 0 || reward <= 0) {
    return {
      rr: "Invalid",
      risk: Math.abs(risk).toFixed(2),
      reward: Math.abs(reward).toFixed(2),
      riskPct: Math.abs(riskPct).toFixed(2),
      rewardPct: Math.abs(rewardPct).toFixed(2),
      isValid: false,
      error: isDemand
        ? (s >= e ? "Stop Loss must be lower than Entry price in DEMAND (Long) setup." : "Target must be higher than Entry price in DEMAND (Long) setup.")
        : (s <= e ? "Stop Loss must be higher than Entry price in SUPPLY (Short) setup." : "Target must be lower than Entry price in SUPPLY (Short) setup.")
    };
  }
  
  const ratio = reward / risk;
  const formattedRatio = Number.isInteger(ratio) ? ratio : ratio.toFixed(1);
  return {
    rr: `${formattedRatio}:1`,
    risk: risk.toFixed(2),
    reward: reward.toFixed(2),
    riskPct: riskPct.toFixed(2),
    rewardPct: rewardPct.toFixed(2),
    isValid: true,
    error: ""
  };
};

const AdminAlertsPage = () => {
  const { theme } = useTheme();
  const isLight = theme === "light";
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Tabs & Filtering
  const [mainTab, setMainTab] = useState("pending"); // "pending" | "approved" | "rejected" | "all"
  const [subFilter, setSubFilter] = useState("all"); // "all" | "DEMAND" | "SUPPLY"
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination (H4: zero-lag smooth browsing)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    setCurrentPage(1);
  }, [mainTab, subFilter, searchTerm]);
  
  // Selection for bulk operations
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Pro Setup Edit & Review Modal
  const [editModal, setEditModal] = useState(null); // alert object
  const [editForm, setEditForm] = useState({
    entry: "",
    stop_loss: "",
    target: "",
    zone_type: "DEMAND",
    trade_type: "LONG",
    timeframe: "15 Min",
    score: "9.0",
    setup_type: "MIT",
    admin_remark: ""
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // Close Trade Modal
  const [closeModal, setCloseModal] = useState(null); // alert object

  // Create Test Alert Modal
  const [createModal, setCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    script_name: "TATAMOTORS",
    trade_type: "LONG",
    zone_type: "DEMAND",
    timeframe: "15 Min",
    entry: "1000",
    stop_loss: "980",
    target: "1050",
    score: "9.0",
    setup_type: "MIT",
    status: "pending"
  });

  useEffect(() => {
    fetchAlerts();
    const iv = setInterval(() => fetchAlerts(true), 15000);
    return () => clearInterval(iv);
  }, []);

  // Clear selections whenever tab or subfilter changes
  useEffect(() => {
    setSelectedIds([]);
  }, [mainTab, subFilter]);

  const fetchAlerts = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/drishti/alerts/all?limit=3000`, { withCredentials: true });
      setAlerts(asArray(res.data, "alerts"));
    } catch (err) {
      if (!silent) toast.error("Failed to load alerts");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleManualRefresh = () => {
    setRefreshing(true);
    fetchAlerts();
  };

  // Counts
  const counts = useMemo(() => {
    const pending = alerts.filter(a => a.status === "pending");
    const approved = alerts.filter(a => a.status === "approved");
    const rejected = alerts.filter(a => a.status === "rejected");
    return {
      all: alerts.length,
      pendingTotal: pending.length,
      pendingDemand: pending.filter(a => (a.zone_type || "").toUpperCase() === "DEMAND").length,
      pendingSupply: pending.filter(a => (a.zone_type || "").toUpperCase() === "SUPPLY").length,
      approvedTotal: approved.length,
      approvedDemand: approved.filter(a => (a.zone_type || "").toUpperCase() === "DEMAND").length,
      approvedSupply: approved.filter(a => (a.zone_type || "").toUpperCase() === "SUPPLY").length,
      rejectedTotal: rejected.length
    };
  }, [alerts]);

  // Filtered Alerts
  const displayedAlerts = useMemo(() => {
    return alerts.filter(alert => {
      // 1. Main Tab Filter
      if (mainTab !== "all" && alert.status !== mainTab) {
        return false;
      }
      // 2. Sub Filter (for Pending & Approved tabs)
      if ((mainTab === "pending" || mainTab === "approved") && subFilter !== "all") {
        if ((alert.zone_type || "").toUpperCase() !== subFilter.toUpperCase()) return false;
      }
      // 3. Search Term
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const script = (alert.script_name || "").toLowerCase();
        const setup = (alert.setup_type || "").toLowerCase();
        if (!script.includes(term) && !setup.includes(term)) return false;
      }
      return true;
    });
  }, [alerts, mainTab, subFilter, searchTerm]);

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(displayedAlerts.length / pageSize));
  const validPage = Math.min(Math.max(1, currentPage), totalPages);

  const paginatedAlerts = useMemo(() => {
    const start = (validPage - 1) * pageSize;
    return displayedAlerts.slice(start, start + pageSize);
  }, [displayedAlerts, validPage, pageSize]);

  // Selection handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const pageIds = paginatedAlerts.map(a => a.alert_id);
    const allPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));
    if (allPageSelected) {
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  // Open Pro Setup Review & Edit Modal
  const openEditModal = (alert) => {
    setEditModal(alert);
    setEditForm({
      entry: alert.entry != null ? String(alert.entry) : "",
      stop_loss: alert.stop_loss != null ? String(alert.stop_loss) : "",
      target: alert.target != null ? String(alert.target) : "",
      zone_type: alert.zone_type || "DEMAND",
      trade_type: alert.trade_type || (alert.zone_type === "SUPPLY" ? "SHORT" : "LONG"),
      timeframe: alert.timeframe || "15 Min",
      score: alert.score != null ? String(alert.score) : "9.0",
      setup_type: alert.setup_type || "MIT",
      admin_remark: alert.admin_remark || ""
    });
  };

  // Direct One-Click Approve
  const handleDirectApprove = async (alert) => {
    try {
      await axios.put(`${API}/drishti/alerts/${alert.alert_id}/approve`, {}, { withCredentials: true });
      toast.success(`⚡ ${alert.script_name} approved and live!`, {
        description: `Entry: ₹${alert.entry} | SL: ₹${alert.stop_loss} | Target: ₹${alert.target}`
      });
      fetchAlerts(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approval failed");
    }
  };

  // Save & Approve from Pro Review Modal
  const handleSaveAndApprove = async () => {
    if (!editModal) return;
    const computed = calculateRR(editForm.entry, editForm.stop_loss, editForm.target, editForm.zone_type);
    if (!computed.isValid) {
      toast.error("Invalid trading levels", { description: computed.error });
      return;
    }
    
    setSavingEdit(true);
    try {
      const payload = {
        entry: parseFloat(editForm.entry),
        stop_loss: parseFloat(editForm.stop_loss),
        target: parseFloat(editForm.target),
        zone_type: editForm.zone_type,
        trade_type: editForm.zone_type === "DEMAND" ? "LONG" : "SHORT",
        timeframe: editForm.timeframe,
        score: editForm.score,
        setup_type: editForm.setup_type,
        remark: editForm.admin_remark,
        rr: computed.rr
      };
      await axios.put(`${API}/drishti/alerts/${editModal.alert_id}/approve`, payload, { withCredentials: true });
      toast.success(`⚡ ${editModal.script_name} updated & approved live!`, {
        description: `Entry: ₹${payload.entry} | SL: ₹${payload.stop_loss} | Target: ₹${payload.target} | RR: ${computed.rr}`
      });
      setEditModal(null);
      fetchAlerts(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve alert");
    } finally {
      setSavingEdit(false);
    }
  };

  // Save Draft (Keep Pending or Update existing)
  const handleSaveDraft = async () => {
    if (!editModal) return;
    const computed = calculateRR(editForm.entry, editForm.stop_loss, editForm.target, editForm.zone_type);
    setSavingEdit(true);
    try {
      const payload = {
        entry: parseFloat(editForm.entry),
        stop_loss: parseFloat(editForm.stop_loss),
        target: parseFloat(editForm.target),
        zone_type: editForm.zone_type,
        trade_type: editForm.zone_type === "DEMAND" ? "LONG" : "SHORT",
        timeframe: editForm.timeframe,
        score: editForm.score,
        setup_type: editForm.setup_type,
        admin_remark: editForm.admin_remark,
        rr: computed.rr
      };
      await axios.put(`${API}/drishti/alerts/${editModal.alert_id}/update`, payload, { withCredentials: true });
      toast.success(`${editModal.script_name} levels updated successfully`);
      setEditModal(null);
      fetchAlerts(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update alert");
    } finally {
      setSavingEdit(false);
    }
  };

  // Reject Single Alert
  const handleRejectAlert = async (alertId, remark = "") => {
    try {
      await axios.put(`${API}/drishti/alerts/${alertId}/reject`, { remark }, { withCredentials: true });
      toast.success("Alert rejected");
      if (editModal) setEditModal(null);
      fetchAlerts(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rejection failed");
    }
  };

  // Bulk Approval
  const handleBulkApprove = async (mode = "selected", targetZone = null) => {
    setBulkLoading(true);
    try {
      let payload = {};
      if (mode === "selected") {
        if (selectedIds.length === 0) {
          toast.error("No alerts selected");
          return;
        }
        payload = { alert_ids: selectedIds };
      } else if (mode === "zone") {
        payload = { zone_type: targetZone };
      }

      const res = await axios.put(`${API}/drishti/alerts/bulk-approve`, payload, { withCredentials: true });
      toast.success(res.data.message || "Alerts approved successfully");
      setSelectedIds([]);
      fetchAlerts(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk approval failed");
    } finally {
      setBulkLoading(false);
    }
  };

  // Bulk Rejection
  const handleBulkReject = async () => {
    if (selectedIds.length === 0) {
      toast.error("No alerts selected");
      return;
    }
    if (!window.confirm(`Reject ${selectedIds.length} selected alert(s)?`)) return;
    setBulkLoading(true);
    try {
      const res = await axios.put(`${API}/drishti/alerts/bulk-reject`, { alert_ids: selectedIds }, { withCredentials: true });
      toast.success(res.data.message || "Alerts rejected");
      setSelectedIds([]);
      fetchAlerts(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk reject failed");
    } finally {
      setBulkLoading(false);
    }
  };

  // Single Delete
  const handleDeleteAlert = async (alertId) => {
    if (!window.confirm("Permanently delete this alert? It will immediately vanish for all users.")) return;
    try {
      await axios.delete(`${API}/drishti/alerts/${alertId}`, { withCredentials: true });
      toast.success("Alert permanently deleted");
      setAlerts(prev => prev.filter(a => (a.alert_id || a.id) !== alertId));
      setSelectedIds(prev => prev.filter(id => id !== alertId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete alert");
    }
  };

  // Reopen Closed Alert
  const handleReopenTrade = async (alertId) => {
    try {
      await axios.put(`${API}/drishti/alerts/${alertId}/reopen`, {}, { withCredentials: true });
      toast.success("Trade reopened and restored to live state!");
      fetchAlerts(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reopen trade");
    }
  };

  // Bulk Delete
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} alert(s) permanently?`)) return;
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API}/drishti/alerts/bulk-delete`, { alert_ids: selectedIds }, { withCredentials: true });
      toast.success(res.data.message || "Alerts deleted");
      setAlerts(prev => prev.filter(a => !selectedIds.includes(a.alert_id || a.id)));
      setSelectedIds([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkLoading(false);
    }
  };

  // Create Test Alert
  const handleCreateAlert = async () => {
    if (!createForm.script_name.trim()) { toast.error("Script name is required"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/drishti/alerts/create`, {
        ...createForm,
        entry: parseFloat(createForm.entry),
        stop_loss: parseFloat(createForm.stop_loss),
        target: parseFloat(createForm.target),
        cmp: parseFloat(createForm.entry)
      }, { withCredentials: true });
      toast.success("Test alert created successfully!");
      setCreateModal(false);
      fetchAlerts(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create alert");
    } finally {
      setCreating(false);
    }
  };

  // Computed RR for the open edit modal
  const computedRR = useMemo(() => {
    return calculateRR(editForm.entry, editForm.stop_loss, editForm.target, editForm.zone_type);
  }, [editForm.entry, editForm.stop_loss, editForm.target, editForm.zone_type]);

  if (loading && alerts.length === 0) {
    return (
      <DashboardLayout title="Drishti AI Alerts">
        <div className="flex items-center justify-center h-80">
          <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Drishti AI Alerts">
      <div className="space-y-4 max-w-7xl mx-auto font-sans" data-testid="drishti-alerts-page">
        
        {/* Header Terminal Card */}
        <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 border px-5 py-4 rounded-2xl shadow-xs transition-all ${
          isLight ? "bg-white border-slate-200/80" : "bg-zinc-900 border-zinc-800"
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${
              isLight ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-emerald-950/60 text-emerald-400 border-emerald-800"
            }`}>
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className={`text-base sm:text-lg font-bold tracking-tight ${isLight ? "text-slate-900" : "text-white"}`}>
                  Drishti AI Alerts Terminal
                </h1>
                {counts.pendingTotal > 0 && (
                  <Badge variant="outline" className={`text-[10px] px-2 py-0.5 font-mono font-medium rounded-full ${
                    isLight ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-amber-950/60 text-amber-400 border-amber-800"
                  }`}>
                    {counts.pendingTotal} Pending Approval
                  </Badge>
                )}
              </div>
              <p className={`text-xs mt-0.5 ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                Review, edit Entry/SL/Target levels, approve institutional setups, and broadcast live alerts to traders.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={handleManualRefresh}
              disabled={refreshing}
              className={`text-xs font-medium h-9 px-3 rounded-xl border ${
                isLight ? "border-slate-200 text-slate-700 hover:bg-slate-50" : "border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              }`}
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin text-emerald-600" : "text-slate-500"}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() => setCreateModal(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs h-9 px-3.5 rounded-xl shadow-xs transition-colors flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" /> Create Alert
            </Button>
          </div>
        </div>

        {/* Navigation Tabs & Search */}
        <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b pb-3 ${
          isLight ? "border-slate-200" : "border-zinc-800"
        }`}>
          <div className="flex items-center gap-1.5 overflow-x-auto custom-scrollbar pb-0.5 text-xs">
            {[
              { key: "pending", label: "Pending", count: counts.pendingTotal, color: "text-amber-600" },
              { key: "approved", label: "Approved", count: counts.approvedTotal, color: "text-emerald-600" },
              { key: "rejected", label: "Rejected", count: counts.rejectedTotal, color: "text-rose-600" },
              { key: "all", label: "All Alerts", count: counts.all, color: "text-slate-500" }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setMainTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${
                  mainTab === tab.key
                    ? (isLight ? "bg-slate-900 text-white shadow-xs font-semibold" : "bg-white text-zinc-900 font-semibold shadow-xs")
                    : (isLight ? "text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-slate-200/60" : "text-zinc-400 hover:text-white hover:bg-zinc-800 border border-zinc-800")
                }`}
              >
                <span>{tab.label}</span>
                <span className={`px-1.5 py-0.2 rounded text-[10px] font-mono font-medium ${
                  mainTab === tab.key
                    ? (isLight ? "bg-slate-800 text-white" : "bg-zinc-200 text-zinc-900")
                    : (isLight ? "bg-slate-100 text-slate-700" : "bg-zinc-800 text-zinc-300")
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Search Input */}
          <div className="relative w-full sm:w-60">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search symbol (e.g. SUNTV)..."
              className="pl-8 h-8 text-xs bg-white dark:bg-[#0D0D12] border-gray-200 dark:border-white/10 font-mono"
            />
          </div>
        </div>

        {/* Sub-Filters & Bulk Actions Bar */}
        {(mainTab === "pending" || mainTab === "approved") && (
          <div className="bg-gray-50/80 dark:bg-[#101017] border border-gray-200 dark:border-white/[0.06] p-2.5 rounded-xl flex flex-col md:flex-row md:items-center justify-between gap-2.5">
            
            {/* Sub-filter pills */}
            <div className="flex items-center gap-1 overflow-x-auto">
              <span className="text-[10px] font-bold text-gray-400 dark:text-[#636366] uppercase mr-1 flex items-center gap-1 shrink-0">
                <Filter className="w-3 h-3" /> Filter:
              </span>
              <button
                onClick={() => setSubFilter("all")}
                className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all ${
                  subFilter === "all"
                    ? "bg-white dark:bg-white/10 text-gray-900 dark:text-white border border-gray-200 dark:border-white/10 shadow-xs"
                    : "text-gray-500 dark:text-[#8E8E93] hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                All {mainTab === "pending" ? `(${counts.pendingTotal})` : `(${counts.approvedTotal})`}
              </button>
              
              <button
                onClick={() => setSubFilter("DEMAND")}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold flex items-center gap-1 transition-all ${
                  subFilter === "DEMAND"
                    ? isLight
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold"
                      : "bg-emerald-950/60 text-emerald-400 border border-emerald-800 font-bold"
                    : isLight
                      ? "text-emerald-700/80 hover:text-emerald-700 hover:bg-emerald-50/50"
                      : "text-emerald-400/80 hover:text-emerald-300 hover:bg-emerald-950/30"
                }`}
              >
                <TrendingUp className="w-3 h-3" /> Demand {mainTab === "pending" ? `(${counts.pendingDemand})` : `(${counts.approvedDemand})`}
              </button>

              <button
                onClick={() => setSubFilter("SUPPLY")}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold flex items-center gap-1 transition-all ${
                  subFilter === "SUPPLY"
                    ? isLight
                      ? "bg-rose-50 text-rose-700 border border-rose-200 font-bold"
                      : "bg-rose-950/60 text-rose-400 border border-rose-800 font-bold"
                    : isLight
                      ? "text-rose-700/80 hover:text-rose-700 hover:bg-rose-50/50"
                      : "text-rose-400/80 hover:text-rose-300 hover:bg-rose-950/30"
                }`}
              >
                <TrendingDown className="w-3 h-3" /> Supply {mainTab === "pending" ? `(${counts.pendingSupply})` : `(${counts.approvedSupply})`}
              </button>
            </div>

            {/* Actions for Pending */}
            {mainTab === "pending" && (
              <div className="flex flex-wrap items-center gap-1.5">
                {selectedIds.length > 0 && (
                  <>
                    <Button
                      size="sm"
                      onClick={() => handleBulkApprove("selected")}
                      disabled={bulkLoading}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs h-7.5 px-3 rounded-lg shadow-xs"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                      Approve Selected ({selectedIds.length})
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleBulkReject}
                      disabled={bulkLoading}
                      className={`font-medium text-xs h-7.5 px-2.5 rounded-lg ${
                        isLight
                          ? "text-rose-600 hover:bg-rose-50"
                          : "text-rose-400 hover:bg-rose-950/40"
                      }`}
                    >
                      <XCircle className="w-3.5 h-3.5 mr-1" />
                      Reject Selected ({selectedIds.length})
                    </Button>
                  </>
                )}

                {selectedIds.length === 0 && (
                  <>
                    {counts.pendingDemand > 0 && (
                      <Button
                        size="sm"
                        onClick={() => handleBulkApprove("zone", "DEMAND")}
                        disabled={bulkLoading}
                        className={`font-medium text-[11px] h-7.5 px-2.5 rounded-lg transition-all border ${
                          isLight
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                            : "bg-emerald-950/40 text-emerald-400 border-emerald-800/80 hover:bg-emerald-950/60"
                        }`}
                      >
                        ⚡ Approve All Demand ({counts.pendingDemand})
                      </Button>
                    )}

                    {counts.pendingSupply > 0 && (
                      <Button
                        size="sm"
                        onClick={() => handleBulkApprove("zone", "SUPPLY")}
                        disabled={bulkLoading}
                        className={`font-medium text-[11px] h-7.5 px-2.5 rounded-lg transition-all border ${
                          isLight
                            ? "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
                            : "bg-rose-950/40 text-rose-400 border-rose-800/80 hover:bg-rose-950/60"
                        }`}
                      >
                        ⚡ Approve All Supply ({counts.pendingSupply})
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Actions for Approved */}
            {mainTab === "approved" && selectedIds.length > 0 && (
              <div className="flex items-center gap-1.5">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleBulkDelete}
                  disabled={bulkLoading}
                  className="bg-rose-600 hover:bg-rose-700 text-white font-medium text-xs h-7.5 px-2.5 rounded-lg"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" />
                  Delete Selected ({selectedIds.length})
                </Button>
              </div>
            )}

          </div>
        )}

        {/* Select All Row */}
        {displayedAlerts.length > 0 && (mainTab === "pending" || mainTab === "approved") && (
          <div className="flex items-center justify-between px-1 text-[11px] text-slate-500">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-1.5 hover:text-slate-900 transition-colors"
            >
              {selectedIds.length === displayedAlerts.length && displayedAlerts.length > 0 ? (
                <CheckSquare className="w-3.5 h-3.5 text-emerald-600" />
              ) : (
                <Square className="w-3.5 h-3.5 text-slate-400" />
              )}
              <span>Select All Visible ({displayedAlerts.length})</span>
            </button>
            {selectedIds.length > 0 && (
              <span className="font-mono text-[10px] text-emerald-600 font-bold">
                {selectedIds.length} of {displayedAlerts.length} selected
              </span>
            )}
          </div>
        )}

        {/* Sleek Alert Cards List */}
        {displayedAlerts.length === 0 ? (
          <Card className="bg-white dark:bg-[#0D0D14] border-gray-200 dark:border-white/[0.06] rounded-xl">
            <CardContent className="p-10 text-center">
              <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-white/5 flex items-center justify-center mx-auto mb-2">
                <Filter className="w-5 h-5 text-gray-400" />
              </div>
              <p className="text-xs font-bold text-gray-900 dark:text-white font-outfit">No alerts found</p>
              <p className="text-[11px] text-gray-500 dark:text-[#8E8E93] mt-0.5">
                No alerts matching "{mainTab}" with "{subFilter}" filter.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {paginatedAlerts.map(alert => {
              const isSelected = selectedIds.includes(alert.alert_id);
              const isDemand = alert.zone_type === "DEMAND";
              
              return (
                <Card
                  key={alert.alert_id}
                  className={`transition-all rounded-2xl overflow-hidden border relative ${
                    isSelected
                      ? isLight ? "border-emerald-300 bg-emerald-50/20 shadow-xs" : "border-emerald-700 bg-emerald-950/20 shadow-xs"
                      : isLight ? "bg-white border-slate-200/80 hover:border-slate-300 hover:shadow-xs" : "bg-zinc-900 border-zinc-800 hover:border-zinc-700"
                  }`}
                  data-testid={`admin-alert-${alert.alert_id}`}
                >
                  {/* Status Indicator Bar on Left Edge */}
                  <div
                    className={`absolute left-0 top-0 bottom-0 w-1 ${
                      alert.status === "pending"
                        ? "bg-amber-500"
                        : alert.status === "approved"
                          ? "bg-emerald-500"
                          : "bg-rose-500 opacity-70"
                    }`}
                  />

                  <CardContent className="p-3.5 sm:p-4 pl-4 sm:pl-5">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                      
                      {/* Left: Checkbox + Stock Symbol + Interactive Level Badges */}
                      <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
                        {/* Checkbox */}
                        {(mainTab === "pending" || mainTab === "approved") && (
                          <button
                            type="button"
                            onClick={() => toggleSelect(alert.alert_id)}
                            className={`p-0.5 transition-colors shrink-0 mt-0.5 sm:mt-0 ${
                              isLight ? "text-slate-400 hover:text-slate-700" : "text-zinc-500 hover:text-zinc-200"
                            }`}
                          >
                            {isSelected ? (
                              <CheckSquare className="w-4 h-4 text-emerald-500" />
                            ) : (
                              <Square className="w-4 h-4" />
                            )}
                          </button>
                        )}

                        {/* Direction Indicator */}
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 border ${
                          isDemand
                            ? isLight
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-emerald-950/50 text-emerald-400 border-emerald-800/70"
                            : isLight
                              ? "bg-rose-50 text-rose-700 border-rose-200"
                              : "bg-rose-950/50 text-rose-400 border-rose-800/70"
                        }`}>
                          {isDemand ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                        </div>

                        {/* Core Stock Details & Levels */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                            <span className={`text-sm sm:text-base font-bold tracking-tight ${isLight ? "text-slate-900" : "text-white"}`}>
                              {alert.script_name || "N/A"}
                            </span>
                            <Badge className={`border text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md ${
                              isDemand
                                ? isLight
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-emerald-950/50 text-emerald-400 border-emerald-800/70"
                                : isLight
                                  ? "bg-rose-50 text-rose-700 border-rose-200"
                                  : "bg-rose-950/50 text-rose-400 border-rose-800/70"
                            }`}>
                              {alert.zone_type || "DEMAND"}
                            </Badge>
                            {alert.timeframe && (
                              <Badge variant="outline" className={`text-[10px] font-mono px-1.5 py-0.5 border ${
                                isLight
                                  ? "text-slate-600 border-slate-200 bg-white"
                                  : "text-zinc-300 border-zinc-700 bg-zinc-800/60"
                              }`}>
                                <Clock className="w-2.5 h-2.5 mr-1" />{alert.timeframe}
                              </Badge>
                            )}
                            {alert.score && (
                              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${
                                isLight
                                  ? "text-slate-600 bg-slate-100 border-slate-200"
                                  : "text-zinc-300 bg-zinc-800 border-zinc-700"
                              }`}>
                                Score: {alert.score}
                              </span>
                            )}
                            {alert.setup_type && (
                              <Badge className={`text-[10px] font-mono px-1.5 py-0.5 border ${
                                isLight
                                  ? "bg-amber-50 text-amber-700 border-amber-200"
                                  : "bg-amber-950/50 text-amber-400 border-amber-800/70"
                              }`}>
                                <Zap className="w-2.5 h-2.5 mr-1" />{alert.setup_type}
                              </Badge>
                            )}
                          </div>

                          {/* Interactive Price Level Badges Strip */}
                          <div className="flex flex-wrap items-center gap-2 text-xs font-mono">
                            <button
                              onClick={() => openEditModal(alert)}
                              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border transition-colors group cursor-pointer ${
                                isLight
                                  ? "bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-900"
                                  : "bg-zinc-800/90 hover:bg-zinc-750 border-zinc-700/80 text-zinc-100 shadow-xs"
                              }`}
                              title="Click to edit Entry"
                            >
                              <span className={`text-[10px] uppercase font-semibold ${isLight ? "text-slate-400" : "text-zinc-400"}`}>Entry:</span>
                              <span className={`font-bold ${isLight ? "text-slate-900" : "text-white"}`}>₹{alert.entry ?? "—"}</span>
                              <PenSquare className={`w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity ml-0.5 ${isLight ? "text-slate-400" : "text-zinc-400"}`} />
                            </button>

                            <button
                              onClick={() => openEditModal(alert)}
                              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border transition-colors group cursor-pointer ${
                                isLight
                                  ? "bg-rose-50/70 hover:bg-rose-100/70 border-rose-200 text-rose-700"
                                  : "bg-rose-950/40 hover:bg-rose-950/60 border-rose-800/60 text-rose-300 shadow-xs"
                              }`}
                              title="Click to edit Stop Loss"
                            >
                              <span className={`text-[10px] uppercase font-semibold ${isLight ? "text-rose-600" : "text-rose-400"}`}>SL:</span>
                              <span className={`font-bold ${isLight ? "text-rose-700" : "text-rose-300"}`}>₹{alert.stop_loss ?? "—"}</span>
                              <PenSquare className={`w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity ml-0.5 ${isLight ? "text-rose-500" : "text-rose-400"}`} />
                            </button>

                            <button
                              onClick={() => openEditModal(alert)}
                              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border transition-colors group cursor-pointer ${
                                isLight
                                  ? "bg-emerald-50/70 hover:bg-emerald-100/70 border-emerald-200 text-emerald-700"
                                  : "bg-emerald-950/40 hover:bg-emerald-950/60 border-emerald-800/60 text-emerald-300 shadow-xs"
                              }`}
                              title="Click to edit Target"
                            >
                              <span className={`text-[10px] uppercase font-semibold ${isLight ? "text-emerald-700" : "text-emerald-400"}`}>Target:</span>
                              <span className={`font-bold ${isLight ? "text-emerald-700" : "text-emerald-300"}`}>₹{alert.target ?? "—"}</span>
                              <PenSquare className={`w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity ml-0.5 ${isLight ? "text-emerald-600" : "text-emerald-400"}`} />
                            </button>

                            <span className={`flex items-center gap-1 px-2 py-1 rounded-lg border font-bold text-[11px] ${
                              isLight
                                ? "bg-amber-50 border-amber-200 text-amber-700"
                                : "bg-amber-950/40 border-amber-800/60 text-amber-400"
                            }`}>
                              <span className={`text-[9px] uppercase font-medium ${isLight ? "text-amber-600" : "text-amber-400/80"}`}>RR:</span>
                              {alert.rr || "—"}
                            </span>
                          </div>

                          {/* Admin Remark Note if exists */}
                          {alert.admin_remark && (
                            <div className={`mt-2 p-2 rounded-lg text-[11px] flex items-start gap-1.5 border ${
                              isLight
                                ? "bg-slate-50 border-slate-100 text-slate-700"
                                : "bg-zinc-800/50 border-zinc-700/60 text-zinc-300"
                            }`}>
                              <ShieldCheck className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                              <div>
                                <span className="text-amber-500 font-bold uppercase text-[9px] tracking-wider mr-1">Admin Remark:</span>
                                <span className="italic">{alert.admin_remark}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right: Timestamp & Action CTAs */}
                      <div className={`flex items-center justify-between lg:justify-end gap-2 shrink-0 pt-2 lg:pt-0 border-t lg:border-t-0 ${
                        isLight ? "border-slate-100" : "border-zinc-800"
                      }`}>
                        <span className={`text-[10px] font-mono whitespace-nowrap ${isLight ? "text-slate-400" : "text-zinc-500"}`}>
                          {alert.created_at ? new Date(alert.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                        </span>

                        {/* Pending Actions */}
                        {alert.status === "pending" && (
                          <div className="flex items-center gap-1.5">
                            <Button
                              size="sm"
                              onClick={() => handleDirectApprove(alert)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs h-8 px-3 rounded-xl shadow-xs"
                              title="Directly approve with original levels"
                            >
                              <Check className="w-3.5 h-3.5 mr-1" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditModal(alert)}
                              className={`text-xs h-8 px-2.5 rounded-xl font-medium ${
                                isLight
                                  ? "border-slate-200 text-slate-700 hover:bg-slate-50"
                                  : "border-zinc-700 text-zinc-200 hover:bg-zinc-800 hover:text-white"
                              }`}
                              title="Edit Entry, SL, Target before approving"
                            >
                              <SlidersHorizontal className="w-3 h-3 mr-1" /> Edit Setup
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRejectAlert(alert.alert_id)}
                              className={`text-xs h-8 px-2 rounded-xl font-medium ${
                                isLight
                                  ? "text-rose-600 hover:bg-rose-50"
                                  : "text-rose-400 hover:bg-rose-950/40"
                              }`}
                              title="Reject Alert"
                            >
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}

                        {/* Approved Actions */}
                        {alert.status === "approved" && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {alert.is_closed ? (
                              <Badge className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${
                                isLight
                                  ? "bg-purple-50 text-purple-700 border-purple-200"
                                  : "bg-purple-950/40 text-purple-300 border-purple-800/60"
                              }`}>
                                🎯 {alert.close_reason || "CLOSED"} {alert.close_price ? `@ ₹${alert.close_price}` : ""}
                              </Badge>
                            ) : (
                              <Badge className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${
                                isLight
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-emerald-950/40 text-emerald-300 border-emerald-800/60"
                              }`}>
                                <Check className="w-2.5 h-2.5 mr-1" /> Live
                              </Badge>
                            )}

                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditModal(alert)}
                              className={`h-8 px-2.5 text-xs rounded-xl font-medium ${
                                isLight
                                  ? "text-slate-700 hover:bg-slate-50 border-slate-200"
                                  : "text-zinc-200 hover:bg-zinc-800 border-zinc-700 hover:text-white"
                              }`}
                              title="Edit Live Levels & Remark"
                            >
                              <SlidersHorizontal className="w-3 h-3 mr-1" /> Edit Levels
                            </Button>

                            {alert.is_closed ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReopenTrade(alert.alert_id)}
                                className="h-7.5 px-2.5 text-xs text-purple-400 border-purple-500/30 hover:bg-purple-500/10 font-medium"
                                title="Reopen Trade to Live Status"
                              >
                                <RotateCcw className="w-3 h-3 mr-1" /> Re-open
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setCloseModal(alert)}
                                className="h-7.5 px-2.5 text-xs text-purple-400 border-purple-500/30 hover:bg-purple-500/15 font-medium"
                                title="Close Trade (Target Hit / SL Hit / Profit Booked)"
                              >
                                <Target className="w-3 h-3 mr-1" /> Close Trade
                              </Button>
                            )}

                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteAlert(alert.alert_id)}
                              className="h-7.5 px-2 text-xs text-[#FF3B30] hover:bg-[#FF3B30]/10"
                              title="Delete Alert (Removes from all users immediately)"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}

                        {/* Rejected Actions */}
                        {alert.status === "rejected" && (
                          <div className="flex items-center gap-1.5">
                            <Badge className="bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/25 text-[10px] font-bold px-2 py-0.5">
                              Rejected
                            </Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditModal(alert)}
                              className={`h-7.5 px-2.5 text-xs font-medium ${
                                isLight
                                  ? "text-slate-600 hover:text-slate-900 border-slate-200 hover:bg-slate-50"
                                  : "text-zinc-400 hover:text-white border-zinc-700 hover:bg-zinc-800"
                              }`}
                              title="Re-evaluate & Edit Setup"
                            >
                              <SlidersHorizontal className="w-3 h-3 mr-1" /> Re-evaluate
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteAlert(alert.alert_id)}
                              className="h-7.5 px-2 text-xs text-[#FF3B30] hover:bg-[#FF3B30]/10"
                              title="Delete Alert"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}

                      </div>

                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination Bar (H4) */}
        {displayedAlerts.length > 0 && (
          <div className={`flex flex-col sm:flex-row items-center justify-between gap-3 p-3.5 rounded-2xl border ${
            isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900 border-zinc-800"
          }`}>
            <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
              <span>
                Showing <strong className={isLight ? "text-slate-800" : "text-white"}>{((validPage - 1) * pageSize) + 1}</strong>–<strong className={isLight ? "text-slate-800" : "text-white"}>{Math.min(validPage * pageSize, displayedAlerts.length)}</strong> of <strong className={isLight ? "text-slate-800" : "text-white"}>{displayedAlerts.length}</strong> alerts
              </span>
              <span className="text-slate-300 dark:text-zinc-700">|</span>
              <div className="flex items-center gap-1.5">
                <span>Per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className={`text-xs font-mono font-semibold py-1 px-2 rounded-lg border outline-none ${
                    isLight ? "bg-slate-50 border-slate-200 text-slate-800" : "bg-zinc-800 border-zinc-700 text-zinc-200"
                  }`}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={validPage <= 1}
                className={`h-8 w-8 p-0 rounded-lg ${isLight ? "border-slate-200" : "border-zinc-700"}`}
                title="First page"
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={validPage <= 1}
                className={`h-8 px-2.5 rounded-lg text-xs flex items-center gap-1 ${isLight ? "border-slate-200" : "border-zinc-700"}`}
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </Button>

              <div className="flex items-center gap-1 px-2">
                <span className="text-xs font-mono">
                  Page <strong className={isLight ? "text-slate-900" : "text-white"}>{validPage}</strong> of <strong className={isLight ? "text-slate-900" : "text-white"}>{totalPages}</strong>
                </span>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={validPage >= totalPages}
                className={`h-8 px-2.5 rounded-lg text-xs flex items-center gap-1 ${isLight ? "border-slate-200" : "border-zinc-700"}`}
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={validPage >= totalPages}
                className={`h-8 w-8 p-0 rounded-lg ${isLight ? "border-slate-200" : "border-zinc-700"}`}
                title="Last page"
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

      </div>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* ── NEXT-LEVEL PRO SETUP REVIEWER & EDIT MODAL ── */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {editModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-3 sm:p-4 backdrop-blur-sm animate-in fade-in duration-200 overflow-y-auto overscroll-contain" data-lenis-prevent="true">
          <div data-lenis-prevent="true" className={`rounded-3xl p-6 w-full max-w-xl border shadow-2xl space-y-4 max-h-[88vh] overflow-y-auto overscroll-contain custom-scrollbar ${
            isLight ? "bg-white border-slate-200 text-slate-900" : "bg-zinc-900 border-zinc-800 text-white"
          }`}>
            
            {/* Modal Header */}
            <div className={`flex items-center justify-between border-b pb-3.5 ${
              isLight ? "border-slate-100" : "border-zinc-800"
            }`}>
              <div className="flex items-center gap-2.5">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border ${
                  editForm.zone_type === "DEMAND"
                    ? isLight
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-emerald-950/50 text-emerald-400 border-emerald-800/70"
                    : isLight
                      ? "bg-rose-50 text-rose-700 border-rose-200"
                      : "bg-rose-950/50 text-rose-400 border-rose-800/70"
                }`}>
                  {editForm.zone_type === "DEMAND" ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-bold tracking-tight">{editModal.script_name}</h2>
                    <Badge variant="outline" className={`text-[10px] font-mono border ${
                      isLight ? "text-slate-600 border-slate-200" : "text-zinc-400 border-zinc-700"
                    }`}>NSE</Badge>
                    <Badge className={`border text-[10px] font-bold uppercase rounded-md ${
                      editForm.zone_type === "DEMAND"
                        ? isLight
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-emerald-950/50 text-emerald-400 border-emerald-800/70"
                        : isLight
                          ? "bg-rose-50 text-rose-700 border-rose-200"
                          : "bg-rose-950/50 text-rose-400 border-rose-800/70"
                    }`}>
                      {editForm.zone_type}
                    </Badge>
                  </div>
                  <p className={`text-xs mt-0.5 ${isLight ? "text-slate-500" : "text-zinc-400"}`}>Review and customize exact trade parameters before publishing.</p>
                </div>
              </div>
              <button
                onClick={() => setEditModal(null)}
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                  isLight ? "hover:bg-slate-100 text-slate-400 hover:text-slate-700" : "hover:bg-zinc-800 text-zinc-400 hover:text-white"
                }`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Zone & Timeframe Quick Toggles */}
            <div className={`grid grid-cols-2 gap-3 p-3 rounded-2xl border ${
              isLight ? "bg-slate-50/70 border-slate-100" : "bg-zinc-950/60 border-zinc-800"
            }`}>
              <div>
                <Label className={`text-[11px] font-mono uppercase font-semibold ${isLight ? "text-slate-500" : "text-zinc-400"}`}>Zone Direction</Label>
                <div className="grid grid-cols-2 gap-1.5 mt-1">
                  <button
                    type="button"
                    onClick={() => setEditForm(p => ({ ...p, zone_type: "DEMAND", trade_type: "LONG" }))}
                    className={`py-1.5 text-xs font-semibold rounded-xl border transition-all ${
                      editForm.zone_type === "DEMAND"
                        ? isLight
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200 font-bold shadow-xs"
                          : "bg-emerald-950/60 text-emerald-300 border-emerald-800 font-bold shadow-xs"
                        : isLight
                          ? "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                          : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:bg-zinc-800"
                    }`}
                  >
                    DEMAND (Long)
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditForm(p => ({ ...p, zone_type: "SUPPLY", trade_type: "SHORT" }))}
                    className={`py-1.5 text-xs font-semibold rounded-xl border transition-all ${
                      editForm.zone_type === "SUPPLY"
                        ? isLight
                          ? "bg-rose-50 text-rose-700 border-rose-200 font-bold shadow-xs"
                          : "bg-rose-950/60 text-rose-300 border-rose-800 font-bold shadow-xs"
                        : isLight
                          ? "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                          : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:bg-zinc-800"
                    }`}
                  >
                    SUPPLY (Short)
                  </button>
                </div>
              </div>

              <div>
                <Label className={`text-[11px] font-mono uppercase font-semibold ${isLight ? "text-slate-500" : "text-zinc-400"}`}>Timeframe</Label>
                <select
                  value={editForm.timeframe}
                  onChange={e => setEditForm(p => ({ ...p, timeframe: e.target.value }))}
                  className={`w-full border rounded-xl p-1.5 text-xs mt-1 h-8 font-mono outline-none ${
                    isLight ? "bg-white border-slate-200 text-slate-900" : "bg-zinc-800 border-zinc-700 text-white"
                  }`}
                >
                  <option value="5 Min">5 Min</option>
                  <option value="15 Min">15 Min</option>
                  <option value="1H">1 Hour</option>
                  <option value="Daily">Daily (1D)</option>
                  <option value="Weekly">Weekly (1W)</option>
                </select>
              </div>
            </div>

            {/* 3 Core Trade Levels Grid */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className={`text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${isLight ? "text-slate-700" : "text-zinc-300"}`}>
                  <SlidersHorizontal className="w-3.5 h-3.5 text-emerald-600" /> Setup Price Levels
                </span>
                <span className="text-[11px] font-mono text-slate-400">Recalibrate trade trigger & limits</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Entry Price */}
                <div className={`p-3 rounded-2xl border ${
                  isLight ? "bg-slate-50/70 border-slate-200/80" : "bg-zinc-950/60 border-zinc-800"
                }`}>
                  <div className="flex items-center justify-between mb-1">
                    <Label className={`text-[11px] font-mono uppercase font-medium ${isLight ? "text-slate-500" : "text-zinc-400"}`}>Entry Price</Label>
                    <span className="text-[10px] font-mono text-slate-400">₹ Level</span>
                  </div>
                  <Input
                    type="number"
                    step="0.05"
                    value={editForm.entry}
                    onChange={e => setEditForm(p => ({ ...p, entry: e.target.value }))}
                    className={`border text-base font-bold font-mono h-9 px-2.5 rounded-xl ${
                      isLight ? "bg-white border-slate-200 text-slate-900" : "bg-zinc-900 border-zinc-700 text-white"
                    }`}
                    placeholder="0.00"
                  />
                  <p className="text-[10px] text-slate-400 font-mono mt-1">Trigger Price</p>
                </div>

                {/* Stop Loss */}
                <div className={`p-3 rounded-2xl border ${
                  isLight ? "bg-rose-50/40 border-rose-200/80" : "bg-rose-950/20 border-rose-900/40"
                }`}>
                  <div className="flex items-center justify-between mb-1">
                    <Label className={`text-[11px] font-mono uppercase font-semibold ${isLight ? "text-rose-700" : "text-rose-400"}`}>Stop Loss (SL)</Label>
                    <span className={`text-[10px] font-mono font-bold ${isLight ? "text-rose-700" : "text-rose-400"}`}>
                      {computedRR.risk > 0 ? `-${computedRR.riskPct}%` : "₹ SL"}
                    </span>
                  </div>
                  <Input
                    type="number"
                    step="0.05"
                    value={editForm.stop_loss}
                    onChange={e => setEditForm(p => ({ ...p, stop_loss: e.target.value }))}
                    className={`border text-base font-bold font-mono h-9 px-2.5 rounded-xl ${
                      isLight ? "bg-white border-rose-200 text-rose-700" : "bg-zinc-900 border-rose-800/60 text-rose-400"
                    }`}
                    placeholder="0.00"
                  />
                  <p className={`text-[10px] font-mono mt-1 ${isLight ? "text-rose-600/80" : "text-rose-400/80"}`}>
                    Risk: ₹{computedRR.risk} ({computedRR.riskPct}%)
                  </p>
                </div>

                {/* Target Price */}
                <div className={`p-3 rounded-2xl border ${
                  isLight ? "bg-emerald-50/40 border-emerald-200/80" : "bg-emerald-950/20 border-emerald-900/40"
                }`}>
                  <div className="flex items-center justify-between mb-1">
                    <Label className={`text-[11px] font-mono uppercase font-semibold ${isLight ? "text-emerald-700" : "text-emerald-400"}`}>Target Price</Label>
                    <span className={`text-[10px] font-mono font-bold ${isLight ? "text-emerald-700" : "text-emerald-400"}`}>
                      {computedRR.reward > 0 ? `+${computedRR.rewardPct}%` : "₹ Target"}
                    </span>
                  </div>
                  <Input
                    type="number"
                    step="0.05"
                    value={editForm.target}
                    onChange={e => setEditForm(p => ({ ...p, target: e.target.value }))}
                    className={`border text-base font-bold font-mono h-9 px-2.5 rounded-xl ${
                      isLight ? "bg-white border-emerald-200 text-emerald-700" : "bg-zinc-900 border-emerald-800/60 text-emerald-400"
                    }`}
                    placeholder="0.00"
                  />
                  <p className={`text-[10px] font-mono mt-1 ${isLight ? "text-emerald-700/80" : "text-emerald-400/80"}`}>
                    Reward: ₹{computedRR.reward} (+{computedRR.rewardPct}%)
                  </p>
                </div>
              </div>
            </div>

            {/* Dynamic Risk-Reward Bar Visualizer */}
            <div className={`p-3.5 rounded-2xl border space-y-2 ${
              isLight ? "bg-slate-50/70 border-slate-100" : "bg-zinc-950/60 border-zinc-800"
            }`}>
              <div className="flex items-center justify-between">
                <span className={`text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                  <Target className="w-3.5 h-3.5 text-amber-500" /> Risk : Reward Ratio
                </span>
                <span className={`text-sm font-bold font-mono px-2 py-0.5 rounded-lg border ${
                  computedRR.isValid
                    ? isLight
                      ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-amber-950/50 text-amber-300 border-amber-800/70"
                    : isLight
                      ? "bg-rose-50 text-rose-700 border-rose-200"
                      : "bg-rose-950/50 text-rose-400 border-rose-800/70"
                }`}>
                  {computedRR.rr}
                </span>
              </div>

              {/* Visual Proportion Bar */}
              {computedRR.isValid ? (
                <div>
                  <div className={`h-2 w-full rounded-full overflow-hidden flex ${isLight ? "bg-slate-200" : "bg-zinc-800"}`}>
                    <div
                      style={{ width: `${Math.max(10, Math.min(50, 100 / (1 + parseFloat(computedRR.rr.split(':')[0] || 1))))}%` }}
                      className="bg-rose-500 h-full transition-all"
                      title={`Risk: ${computedRR.riskPct}%`}
                    />
                    <div
                      style={{ width: `${Math.min(90, Math.max(50, 100 - (100 / (1 + parseFloat(computedRR.rr.split(':')[0] || 1)))))}%` }}
                      className="bg-emerald-500 h-full transition-all"
                      title={`Reward: ${computedRR.rewardPct}%`}
                    />
                  </div>
                  <div className={`flex items-center justify-between text-[10px] font-mono mt-1 ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                    <span className={isLight ? "text-rose-600" : "text-rose-400"}>Risk ₹{computedRR.risk} ({computedRR.riskPct}%)</span>
                    <span className={isLight ? "text-emerald-700" : "text-emerald-400"}>Reward ₹{computedRR.reward} (+{computedRR.rewardPct}%)</span>
                  </div>
                </div>
              ) : (
                <div className={`p-2 rounded-lg text-xs flex items-center gap-1.5 border ${
                  isLight ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-rose-950/40 border-rose-900/60 text-rose-300"
                }`}>
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span>{computedRR.error || "Please enter valid numeric price levels."}</span>
                </div>
              )}
            </div>

            {/* Admin Insight / Remark & Quick Chips */}
            <div className="space-y-2">
              <Label className={`text-[11px] font-mono uppercase font-semibold flex items-center gap-1.5 ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                <ShieldCheck className="w-3.5 h-3.5 text-amber-500" /> Admin Insight / Institutional Guidance
              </Label>
              
              {/* Quick Preset Chips */}
              <div className="flex flex-wrap gap-1.5">
                {[
                  "⚡ Strict SL trail after T1 hit",
                  "🎯 High conviction institutional zone",
                  "🚀 Breakout momentum confirmation",
                  "⏳ Wait for 15M candle close retest",
                  "🛡️ Safe risk-defined swing setup"
                ].map(chip => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => setEditForm(p => ({ ...p, admin_remark: chip }))}
                    className={`text-[10px] font-mono px-2 py-1 rounded-lg border transition-colors ${
                      isLight
                        ? "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200"
                        : "bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border-zinc-700"
                    }`}
                  >
                    {chip}
                  </button>
                ))}
              </div>

              <textarea
                value={editForm.admin_remark}
                onChange={e => setEditForm(p => ({ ...p, admin_remark: e.target.value }))}
                placeholder="e.g. Wait for 15M retest before entry. Trail SL aggressively once Target 1 is achieved..."
                className={`w-full h-18 border rounded-xl p-2.5 text-xs font-mono outline-none focus:border-emerald-600 resize-none ${
                  isLight ? "bg-slate-50 border-slate-200 text-slate-900" : "bg-zinc-800 border-zinc-700 text-white"
                }`}
              />
            </div>

            {/* Modal Actions Footer */}
            <div className={`flex flex-col sm:flex-row items-center justify-between gap-2.5 pt-3 border-t ${
              isLight ? "border-slate-100" : "border-zinc-800"
            }`}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRejectAlert(editModal.alert_id, editForm.admin_remark)}
                className={`text-xs w-full sm:w-auto h-9 rounded-xl font-medium ${
                  isLight ? "text-rose-600 hover:bg-rose-50" : "text-rose-400 hover:bg-rose-950/40"
                }`}
              >
                <X className="w-3.5 h-3.5 mr-1" /> Reject Setup
              </Button>

              <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                {editModal.status === "approved" ? (
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSaveDraft}
                    disabled={savingEdit || !computedRR.isValid}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs h-9 px-4 rounded-xl shadow-xs flex items-center gap-1.5"
                  >
                    {savingEdit ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        Save & Update Live Levels
                      </>
                    )}
                  </Button>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSaveDraft}
                      disabled={savingEdit || !computedRR.isValid}
                      className={`text-xs h-9 px-3.5 rounded-xl font-medium ${
                        isLight
                          ? "border-slate-200 text-slate-700 hover:bg-slate-50"
                          : "border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      }`}
                    >
                      Save Draft
                    </Button>

                    <Button
                      type="button"
                      size="sm"
                      onClick={handleSaveAndApprove}
                      disabled={savingEdit || !computedRR.isValid}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs h-9 px-4 rounded-xl shadow-xs flex items-center gap-1.5"
                    >
                      {savingEdit ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <>
                          <Zap className="w-3.5 h-3.5" />
                          Approve & Publish Live
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* ── CREATE TEST ALERT MODAL ── */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {createModal && (
        <div className="fixed inset-0 bg-slate-950/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto overscroll-contain" data-lenis-prevent="true">
          <div data-lenis-prevent="true" className={`rounded-2xl p-5 sm:p-6 w-full max-w-lg border shadow-2xl space-y-4 max-h-[88vh] overflow-y-auto overscroll-contain custom-scrollbar ${
            isLight ? "bg-white text-slate-900 border-slate-200/80" : "bg-zinc-900 text-white border-zinc-800"
          }`}>
            <div className={`flex items-center justify-between border-b pb-3.5 ${
              isLight ? "border-slate-100" : "border-zinc-800"
            }`}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${
                  isLight ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-emerald-950/60 text-emerald-400 border-emerald-800"
                }`}>
                  <Plus className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Create Drishti Alert</h3>
                  <p className={`text-[11px] ${isLight ? "text-slate-500" : "text-zinc-400"}`}>Draft and queue a trade signal for verification</p>
                </div>
              </div>
              <button 
                onClick={() => setCreateModal(false)} 
                className={`p-1.5 rounded-lg transition-colors ${
                  isLight ? "text-slate-400 hover:text-slate-700 hover:bg-slate-100" : "text-zinc-400 hover:text-white hover:bg-zinc-800"
                }`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div>
                <Label className={`uppercase text-[10px] font-semibold tracking-wider ${isLight ? "text-slate-500" : "text-zinc-400"}`}>Script / Symbol *</Label>
                <Input
                  value={createForm.script_name}
                  onChange={e => setCreateForm(p => ({ ...p, script_name: e.target.value.toUpperCase() }))}
                  placeholder="e.g. TATAMOTORS"
                  className={`mt-1.5 uppercase font-bold h-9 text-xs rounded-xl ${
                    isLight ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-zinc-800/80 border-zinc-700 text-white"
                  }`}
                />
              </div>
              <div>
                <Label className={`uppercase text-[10px] font-semibold tracking-wider ${isLight ? "text-slate-500" : "text-zinc-400"}`}>Direction & Zone</Label>
                <select
                  value={createForm.trade_type}
                  onChange={e => setCreateForm(p => ({
                    ...p,
                    trade_type: e.target.value,
                    zone_type: e.target.value === "LONG" ? "DEMAND" : "SUPPLY"
                  }))}
                  className={`w-full rounded-xl p-2 text-xs mt-1.5 h-9 font-medium border ${
                    isLight ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-zinc-800/80 border-zinc-700 text-white"
                  }`}
                >
                  <option value="LONG">LONG (Demand Zone)</option>
                  <option value="SHORT">SHORT (Supply Zone)</option>
                </select>
              </div>
              <div>
                <Label className={`uppercase text-[10px] font-semibold tracking-wider ${isLight ? "text-slate-500" : "text-zinc-400"}`}>Timeframe</Label>
                <select
                  value={createForm.timeframe}
                  onChange={e => setCreateForm(p => ({ ...p, timeframe: e.target.value }))}
                  className={`w-full rounded-xl p-2 text-xs mt-1.5 h-9 font-medium border ${
                    isLight ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-zinc-800/80 border-zinc-700 text-white"
                  }`}
                >
                  <option value="15 Min">15 Min</option>
                  <option value="1H">1H</option>
                  <option value="Daily">Daily</option>
                  <option value="Weekly">Weekly</option>
                </select>
              </div>
              <div>
                <Label className={`uppercase text-[10px] font-semibold tracking-wider ${isLight ? "text-slate-500" : "text-zinc-400"}`}>Initial Status</Label>
                <select
                  value={createForm.status}
                  onChange={e => setCreateForm(p => ({ ...p, status: e.target.value }))}
                  className={`w-full rounded-xl p-2 text-xs mt-1.5 h-9 font-medium border ${
                    isLight ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-zinc-800/80 border-zinc-700 text-white"
                  }`}
                >
                  <option value="pending">Pending (Review in Queue)</option>
                  <option value="approved">Approved (Live for Users)</option>
                </select>
              </div>
              <div>
                <Label className={`uppercase text-[10px] font-semibold tracking-wider ${isLight ? "text-slate-500" : "text-zinc-400"}`}>Entry Price (₹)</Label>
                <Input
                  type="number"
                  step="0.05"
                  value={createForm.entry}
                  onChange={e => setCreateForm(p => ({ ...p, entry: e.target.value }))}
                  className={`mt-1.5 font-mono h-9 text-xs rounded-xl ${
                    isLight ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-zinc-800/80 border-zinc-700 text-white"
                  }`}
                />
              </div>
              <div>
                <Label className={`uppercase text-[10px] font-semibold tracking-wider ${isLight ? "text-slate-500" : "text-zinc-400"}`}>Stop Loss (₹)</Label>
                <Input
                  type="number"
                  step="0.05"
                  value={createForm.stop_loss}
                  onChange={e => setCreateForm(p => ({ ...p, stop_loss: e.target.value }))}
                  className={`mt-1.5 font-mono h-9 text-xs rounded-xl ${
                    isLight ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-zinc-800/80 border-zinc-700 text-white"
                  }`}
                />
              </div>
              <div>
                <Label className={`uppercase text-[10px] font-semibold tracking-wider ${isLight ? "text-slate-500" : "text-zinc-400"}`}>Target Price (₹)</Label>
                <Input
                  type="number"
                  step="0.05"
                  value={createForm.target}
                  onChange={e => setCreateForm(p => ({ ...p, target: e.target.value }))}
                  className={`mt-1.5 font-mono h-9 text-xs rounded-xl ${
                    isLight ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-zinc-800/80 border-zinc-700 text-white"
                  }`}
                />
              </div>
              <div>
                <Label className={`uppercase text-[10px] font-semibold tracking-wider ${isLight ? "text-slate-500" : "text-zinc-400"}`}>Score (1-10)</Label>
                <Input
                  value={createForm.score}
                  onChange={e => setCreateForm(p => ({ ...p, score: e.target.value }))}
                  className={`mt-1.5 font-mono h-9 text-xs rounded-xl ${
                    isLight ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-zinc-800/80 border-zinc-700 text-white"
                  }`}
                />
              </div>
            </div>

            <div className={`flex gap-2.5 justify-end pt-3 border-t ${
              isLight ? "border-slate-100" : "border-zinc-800"
            }`}>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setCreateModal(false)}
                className={`text-xs h-9 px-4 rounded-xl ${
                  isLight ? "text-slate-600 hover:text-slate-900 hover:bg-slate-100" : "text-zinc-400 hover:text-white hover:bg-zinc-800"
                }`}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleCreateAlert}
                disabled={creating}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs h-9 px-4 rounded-xl shadow-xs transition-colors"
              >
                {creating ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : "Create Alert"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* ── CLOSE TRADE MODAL ── */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <CloseTradeModal
        isOpen={!!closeModal}
        alert={closeModal}
        livePrice={closeModal?.cmp}
        onClose={() => setCloseModal(null)}
        onSuccess={() => fetchAlerts(true)}
      />

    </DashboardLayout>
  );
};

export default AdminAlertsPage;
